import type { Suggestion } from '../../api/search'
import type { SearchAction } from './actions'

/**
 * What can be written where the cursor stands — and it takes the place of the hits while it is shown.
 *
 * **Instead of the hits, never beside them.** Two lists on screen at once would need a rule about which
 * of them the arrow keys move, and that is the kind of ambiguity that makes a keyboard interface feel
 * broken. The moment the cursor leaves the token, the hits are back — they were fetched all along.
 *
 * The values come from the service, out of the same registry that reads the filters. So `folder:` offers
 * the real folders of this space, `created:` offers the shapes a date can be written in, and a filter
 * added later appears here without anybody maintaining a second list.
 */
export interface SuggestionsProps {
  readonly listId: string
  readonly suggestions: readonly Suggestion[]
  readonly active: number
  readonly onTake: (value: string) => void
}

export function Suggestions({ listId, suggestions, active, onTake }: SuggestionsProps) {
  return (
    <ul className="suggestions" role="listbox" id={listId} aria-label="What you can write here">
      {suggestions.map((suggestion, index) => (
        <li
          key={suggestion.value}
          role="option"
          id={`${listId}-${index}`}
          aria-selected={index === active}
          className="suggestions__one"
        >
          <button
            type="button"
            className="suggestions__take"
            data-active={index === active}
            // The mouse must not take the focus off the field: the field is where the arrow keys and the
            // next keystroke belong, and a button that stole it would end the search halfway.
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={() => {
              onTake(suggestion.value)
            }}
          >
            <span className="suggestions__label">{suggestion.label}</span>
            {suggestion.label !== suggestion.value && (
              <span className="suggestions__value">{suggestion.value}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * What the search can *do* with this line — one entry, standing among the hits.
 *
 * Among them and not beside them, because that is the only arrangement in which the arrow keys can
 * reach it: two lists on one screen would need a rule about which of them the keyboard moves, and any
 * such rule is a rule somebody has to learn. It stands *first*, because it is the shortest answer to
 * what somebody typed, and it never replaces the hits — a word that is both a place and a word in
 * somebody's documents has to answer as both.
 */
export function Action({
  id,
  action,
  active,
  onRun,
}: {
  readonly id: string
  readonly action: SearchAction
  readonly active: boolean
  readonly onRun: () => void
}) {
  return (
    <li className="actions__one" role="option" id={id} aria-selected={active}>
      <button
        type="button"
        className="actions__do"
        data-active={active}
        onMouseDown={(event) => {
          // The field keeps the focus: the next keystroke belongs in it, and a control that stole
          // the focus would end the search halfway.
          event.preventDefault()
        }}
        onClick={onRun}
      >
        <span className="actions__label">{action.label}</span>
        <span className="actions__hint">{action.hint}</span>
      </button>
    </li>
  )
}
